/**
 * «Comparar campañas» del WhatsApp oficial (`/activa/oficial/campanas/comparar`): una fila por campaña que mandó algo en
 * el período, con los mismos números que su detalle, para ver cuál anduvo mejor. La lista de campañas sólo dice enviados
 * y entregados, y Estadísticas suma por marca o por plantilla.
 *
 * - El rango vive en la URL como en Estadísticas (`?rango=7|30|90|mes|mes_pasado` o `?desde=&hasta=`): el link desde
 *   Estadísticas abre las mismas fechas, y F5 no las pierde. Un cambio de rango descarta la respuesta vieja que llega tarde.
 * - Ordenable por columna (`logic/comparativa`): lo que no tiene dato va siempre al final.
 * - Las tasas, con las bases del detalle: entregados y bajas sobre los enviados; leídos, respondieron y clics sobre los
 *   entregados. Los costos en dólares y la plata de la recompra en pesos: no se dividen entre sí (decisión de Alan).
 * - «Volvieron a comprar» es la misma medición que el Resultado: con la base de ventas (la Base y el Público) dice
 *   cuántos están en ella, la tasa va sobre ésos, y mientras la campaña envía dice que se mide cuando termine.
 * - La ve el equipo y una persona de un cliente (Accesos, rol «comercio»), como la lista. El cliente ve lo que se le
 *   cobró; lo que pagó Deenex sólo el equipo (el backend tampoco se lo manda).
 * - En el celular la tabla se corre de costado dentro de su caja (la página no), con la columna de la campaña fija.
 * - Va adentro de `OficialLayout`, que ya pone el ancho y los márgenes: por eso no usa `Pagina` (duplicaría el margen y
 *   en un celular se come el ancho de la tabla). Sí el encabezado, el vacío y los avisos comunes de Activa.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BarChart3, RefreshCw } from "lucide-react";
import { Aviso, EncabezadoPagina, EstadoVacio } from "../../../components/activa/Pagina";
import { comparativaApi } from "../comparativaApi";
import type { ComparativaCampanas, FilaComparativa } from "../types.comparativa";
import type { RangoFechas } from "../types.estadisticas";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { LEIDO_ACLARACION, rangoDesdeParams, tasaTexto, type ClaveRango } from "../logic/estadisticas";
import {
  NOTA_BASE_DE_VENTAS_COMPARATIVA, NOTA_BASES_COMPARATIVA, NOTA_MONEDAS_COMPARATIVA, NOTA_PERIODO_COMPARATIVA, ORDEN_POR_DEFECTO, SIN_RECOMPRA,
  TIPO_AUDIENCIA, ordenarComparativa, pctTexto, recompraMedida, siguienteOrden, type ClaveOrden, type Orden,
} from "../logic/comparativa";
import { TEXTO_MEDICION_SIN_CONTROL } from "../logic/textos";
import { ars, usd, usdPorMensaje } from "../logic/formato";
import { SelectorRango } from "../components/estadisticas/SelectorRango";
import { ChipSimulado } from "../components/estadisticas/comun";
import { ChipEstadoCampanaOficial } from "../components/ChipEstadoCampanaOficial";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { fecha, num } from "../../campana-nueva/utils/format";

const B = "/activa/oficial";

/** Las columnas, en orden. `sub`: sobre qué va el porcentaje o en qué moneda está. */
const COLUMNAS: { clave: ClaveOrden; titulo: string; sub?: string; numero?: boolean }[] = [
  { clave: "nombre", titulo: "Campaña" },
  { clave: "fecha", titulo: "Fecha" },
  { clave: "audiencia", titulo: "Audiencia" },
  { clave: "enviados", titulo: "Enviados", numero: true },
  { clave: "volvieron", titulo: "Volvieron a comprar", sub: "de los que recibieron", numero: true },
  { clave: "plata", titulo: "Plata", sub: "en pesos", numero: true },
  { clave: "costo", titulo: "Costo", sub: "USD", numero: true },
  { clave: "costoPorEntregado", titulo: "Costo por entregado", sub: "USD", numero: true },
  { clave: "entrega", titulo: "% entregados", sub: "de los enviados", numero: true },
  { clave: "lectura", titulo: "% leídos", sub: "de los entregados", numero: true },
  { clave: "respuesta", titulo: "% respondieron", sub: "de los entregados", numero: true },
  { clave: "clics", titulo: "% clics", sub: "de los entregados", numero: true },
  { clave: "bajas", titulo: "% bajas", sub: "de los enviados", numero: true },
];

/** La primera columna queda fija al correr la tabla de costado (en el celular). */
const FIJA = "sticky left-0 z-10 bg-white shadow-[1px_0_0_0_#f1f5f9]";

export default function ComparativaCampanasPage() {
  const equipo = useEsEquipoDeenex();
  const [sp, setSp] = useSearchParams();
  const spTexto = sp.toString();
  // «Hoy» se fija al cambiar la URL: un re-render a medianoche no mueve el rango solo (como Estadísticas).
  const { rango, clave, error: errorUrl } = useMemo(() => rangoDesdeParams(new URLSearchParams(spTexto), new Date()), [spTexto]);
  const [dato, setDato] = useState<ComparativaCampanas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [orden, setOrden] = useState<Orden>(ORDEN_POR_DEFECTO);
  const seq = useRef(0);

  const cargar = useCallback(async (r: RangoFechas) => {
    const mio = ++seq.current;
    setCargando(true);
    let nuevo: ComparativaCampanas | null = null;
    let fallo = "";
    try {
      const l = leerRespuesta<ComparativaCampanas>(await comparativaApi.campanas(r));
      if (!l.ok) fallo = textoError(l);
      else if (!l.data || !Array.isArray(l.data.filas)) fallo = "El servidor no devolvió datos.";
      else nuevo = l.data;
    } catch (e) {
      fallo = textoError(leerError(e));
    }
    // un rango viejo que llega tarde no se pinta bajo el título del nuevo
    if (mio !== seq.current) return;
    setDato(nuevo);
    setError(fallo);
    setCargando(false);
  }, []);

  // Otro rango: lo anterior se borra (no se muestran números de otras fechas bajo el selector nuevo).
  useEffect(() => {
    setDato(null);
    void cargar(rango);
  }, [cargar, rango]);

  const elegir = (c: ClaveRango) => setSp(new URLSearchParams({ rango: c }), { replace: true });
  const personalizado = (r: RangoFechas) => setSp(new URLSearchParams({ desde: r.desde, hasta: r.hasta }), { replace: true });

  const filas = useMemo(() => ordenarComparativa(dato?.filas, orden), [dato, orden]);

  return (
    <div className="space-y-5">
      <EncabezadoPagina
        antetitulo="Campañas"
        titulo={<span className="inline-flex flex-wrap items-center gap-2">Comparar campañas {dato?.hayDatosSimulados && <ChipSimulado />}</span>}
        bajada="Cada campaña al lado de la otra, con los mismos números que su detalle, para ver cuál anduvo mejor."
        acciones={
          <>
            <Link to={`${B}/campanas`} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ArrowLeft size={15} /> Campañas
            </Link>
            <Link to={`${B}/estadisticas${spTexto ? `?${spTexto}` : ""}`} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <BarChart3 size={15} /> Estadísticas
            </Link>
          </>
        }
      />

      <div className="space-y-2">
        <SelectorRango clave={clave} rango={rango} onElegir={elegir} onPersonalizado={personalizado} />
        {errorUrl && <p className="text-xs text-amber-700" role="status">{errorUrl}</p>}
      </div>

      {cargando ? (
        <div className="space-y-2" aria-busy="true" aria-label="Cargando las campañas">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : error || !dato ? (
        <Aviso tono="error" titulo="No pudimos traer las campañas." testId="comparativa-error"
          accion={
            <button type="button" onClick={() => void cargar(rango)} className="inline-flex items-center gap-1.5 font-semibold underline">
              <RefreshCw size={13} /> Reintentar
            </button>
          }>
          {error || "El servidor no devolvió datos."}
        </Aviso>
      ) : !filas.length ? (
        <EstadoVacio icono={BarChart3} titulo="Ninguna campaña mandó en estas fechas" testId="comparativa-vacia"
          accion={
            <>
              {clave !== "90" && (
                <button type="button" onClick={() => elegir("90")}
                  className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                  Ver los últimos 90 días
                </button>
              )}
              <Link to={`${B}/campanas`} className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center">
                Ir a Campañas
              </Link>
            </>
          }>
          {NOTA_PERIODO_COMPARATIVA} Probá con un rango más largo, o mirá en Campañas las que todavía no salieron.
        </EstadoVacio>
      ) : (
        <div className="space-y-3">
          {dato.truncada && (
            <Aviso tono="atencion" testId="comparativa-truncada">
              Hay más de {num(dato.tope)} campañas en estas fechas: se muestran las {num(dato.tope)} más recientes. Achicá el rango para ver las demás.
            </Aviso>
          )}
          <Tabla filas={filas} orden={orden} onOrden={(c) => setOrden((o) => siguienteOrden(o, c))} equipo={equipo} />
          <div className="space-y-1 text-[11px] text-slate-500">
            <p>{NOTA_PERIODO_COMPARATIVA} {NOTA_BASES_COMPARATIVA}</p>
            <p>{NOTA_MONEDAS_COMPARATIVA}</p>
            <p>«Volvieron a comprar»: {TEXTO_MEDICION_SIN_CONTROL} {NOTA_BASE_DE_VENTAS_COMPARATIVA} {LEIDO_ACLARACION}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Tabla({ filas, orden, onOrden, equipo }: { filas: FilaComparativa[]; orden: Orden; onOrden: (c: ClaveOrden) => void; equipo: boolean }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid="comparativa-tabla">
      <table className="w-full text-sm">
        <caption className="sr-only">Campañas del período, una por fila. Tocá el título de una columna para ordenar.</caption>
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {COLUMNAS.map((c, i) => {
              const activa = orden.clave === c.clave;
              return (
                <th key={c.clave} scope="col" aria-sort={activa ? (orden.dir === "asc" ? "ascending" : "descending") : "none"}
                  className={`px-3 py-2.5 align-bottom whitespace-nowrap ${c.numero ? "text-right" : ""} ${i === 0 ? FIJA : ""}`}>
                  <button type="button" onClick={() => onOrden(c.clave)}
                    className={`uppercase tracking-wider hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 rounded ${activa ? "text-slate-900" : ""} ${c.numero ? "text-right" : "text-left"}`}>
                    {c.titulo}{activa ? (orden.dir === "asc" ? " ↑" : " ↓") : ""}
                    {c.sub && <span className="block text-[10px] font-medium normal-case tracking-normal text-slate-400">{c.sub}</span>}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.map((f) => <FilaTabla key={f.id} f={f} equipo={equipo} />)}
        </tbody>
      </table>
    </div>
  );
}

/** Un porcentaje con su numerador debajo («66,7 %» / «2 personas»). */
function Tasa({ texto, parte }: { texto: string; parte?: string }) {
  return (
    <>
      <span className="text-slate-900">{texto}</span>
      {parte && <span className="block text-[11px] text-slate-400">{parte}</span>}
    </>
  );
}

/** «Volvieron a comprar»: cuántos y la tasa; con la base de ventas, también cuántos están en ella. O por qué no hay. */
function CeldaRecompra({ f }: { f: FilaComparativa }) {
  const r = f.recompra;
  const medida = recompraMedida(f);
  if (medida) {
    const conLaBase = medida.fuente === "base-de-ventas";
    return (
      <>
        <Tasa texto={`${num(medida.volvieron)} de ${num(conLaBase ? Number(medida.enLaBase) : medida.recibieron)}`} parte={`${tasaTexto(medida.tasa)}${medida.parcial ? " · parcial" : ""}`} />
        {conLaBase && <span className="block text-[11px] text-slate-400">{num(Number(medida.enLaBase))} de {num(medida.recibieron)} en la base de ventas</span>}
      </>
    );
  }
  // medida, pero sin números de verdad: nadie está en la base de ventas, o faltan las compras de después
  const texto = r.disponible
    ? r.fuente === "base-de-ventas" && !r.pendienteBase
      ? `Nadie de los ${num(r.recibieron)} está en la base de ventas`
      : SIN_RECOMPRA.SIN_BASE_POSTERIOR
    : SIN_RECOMPRA[r.motivo] ?? SIN_RECOMPRA.SIN_MEDIR;
  const titulo = !r.disponible && r.mensaje ? r.mensaje : texto;
  return (
    <span className="text-xs text-slate-400" title={titulo}>
      Sin medición
      <span className="block text-[11px] whitespace-normal max-w-[11rem] ml-auto">{texto}</span>
    </span>
  );
}

function FilaTabla({ f, equipo }: { f: FilaComparativa; equipo: boolean }) {
  const medida = recompraMedida(f);
  const td = "px-3 py-2.5 align-top text-right tabular-nums whitespace-nowrap";
  return (
    <tr className="group hover:bg-slate-50/60" data-testid={`fila-${f.id}`}>
      <td className={`px-3 py-2.5 align-top min-w-[11rem] max-w-[16rem] ${FIJA} group-hover:bg-slate-50`}>
        <Link to={`${B}/campanas/${encodeURIComponent(f.id)}`} className="font-semibold text-slate-900 hover:text-violet-700 break-words">{f.nombre || "Sin nombre"}</Link>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {f.estado !== "finalizada" && <ChipEstadoCampanaOficial estado={f.estado} />}
          {f.simulado && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Simulada</span>}
        </div>
        {f.plantilla?.nombreVisible && <p className="mt-0.5 text-[11px] text-slate-400 truncate">{f.plantilla.nombreVisible}</p>}
      </td>
      <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">{fecha(f.iniciadaAt ?? undefined)}</td>
      <td className="px-3 py-2.5 align-top min-w-[9rem] max-w-[14rem]">
        <span className="block text-[11px] text-slate-400">{TIPO_AUDIENCIA[f.audiencia?.tipo] ?? "Audiencia"}</span>
        <span className="text-slate-700 break-words">{f.audiencia?.nombre || "—"}</span>
      </td>
      <td className={`${td} text-slate-900`}>{num(f.enviados)}</td>
      <td className={td}><CeldaRecompra f={f} /></td>
      <td className={td}>{medida ? <span className="text-slate-900">{ars(medida.plata)}</span> : <span className="text-xs text-slate-400">Sin medición</span>}</td>
      <td className={td}>
        <span className="text-slate-900">{usd(f.cobradoClienteUSD)}</span>
        {/* Lo que pagó Deenex es del equipo: a una persona de un cliente, sólo lo que se le cobró (como el detalle). */}
        {equipo && typeof f.costoRealUSD === "number" && <span className="block text-[11px] text-slate-400">Deenex pagó {usd(f.costoRealUSD)}</span>}
      </td>
      <td className={`${td} text-slate-700`}>{usdPorMensaje(f.costoPorEntregadoUSD)}</td>
      <td className={td}><Tasa texto={tasaTexto(f.tasas?.entrega)} parte={num(f.entregados)} /></td>
      <td className={td}>{f.entregados > 0 ? <Tasa texto={tasaTexto(f.tasas?.lectura)} parte={num(f.leidos)} /> : "—"}</td>
      <td className={td}>{f.entregados > 0 ? <Tasa texto={tasaTexto(f.tasas?.respuesta)} parte={num(f.respondieron)} /> : "—"}</td>
      <td className={td}>
        {f.clics?.activo && f.clics.tasaClicPct !== null
          ? <Tasa texto={pctTexto(f.clics.tasaClicPct)} parte={`${num(f.clics.unicos)} ${f.clics.unicos === 1 ? "persona" : "personas"}`} />
          : <span className="text-slate-400" title="La plantilla no tiene un botón que cuente los clics">—</span>}
      </td>
      <td className={td}><Tasa texto={tasaTexto(f.tasas?.bajas)} parte={num(f.bajas)} /></td>
    </tr>
  );
}
